/**
 * PHARMAKON — Fournisseur ERP · portal route tree (Module 00, Appendix S)
 *
 * The nested react-router tree mounted at /portail-fournisseur/*. The whole
 * subtree sits behind <RequireSupplierRole> (any of the 7 supplier roles or the
 * two legacy roles); gated sections additionally wrap in <RequireModule> so a
 * missing module renders a real-but-limited PREVIEW (never a dead page).
 *
 * Mounted once from App.tsx:
 *   <Route path="/portail-fournisseur/*" element={<FournisseurRoutes />} />
 *
 * Catalogue / Pré-commandes / Négociations / Clients are migrated in their own
 * modules; until then they render the legacy section inside the new shell via a
 * thin adapter (LegacySection) — no PORTAL_CSS global injection beyond what the
 * legacy component needs, scoped to those routes only.
 */
import * as React from "react";
import { Routes, Route, Navigate, Outlet } from "react-router-dom";

import RequireSupplierRole from "@/components/fournisseur/guards/RequireSupplierRole.jsx";
import RequireModule from "@/components/fournisseur/guards/RequireModule.jsx";
import FournisseurLayout from "@/components/fournisseur/layout/FournisseurLayout.jsx";

const FournisseurDashboard = React.lazy(() => import("@/pages/fournisseur/FournisseurDashboard.jsx"));
const EFactureList = React.lazy(() => import("@/pages/fournisseur/efacture/EFactureList.jsx"));
const EFactureDetail = React.lazy(() => import("@/pages/fournisseur/efacture/EFactureDetail.jsx"));
const EFactureBuilder = React.lazy(() => import("@/pages/fournisseur/efacture/EFactureBuilder.jsx"));
const StaffManagement = React.lazy(() => import("@/pages/fournisseur/equipe/StaffManagement.jsx"));
const NotificationPreferences = React.lazy(() => import("@/pages/fournisseur/parametres/NotificationPreferences.jsx"));

// Module 01 — Upstream Procurement (Approvisionnement)
const ManufacturierList = React.lazy(() => import("@/pages/fournisseur/approvisionnement/ManufacturierList.jsx"));
const PurchaseOrderList = React.lazy(() => import("@/pages/fournisseur/approvisionnement/PurchaseOrderList.jsx"));
const PurchaseOrderBuilder = React.lazy(() => import("@/pages/fournisseur/approvisionnement/PurchaseOrderBuilder.jsx"));
const PurchaseOrderDetail = React.lazy(() => import("@/pages/fournisseur/approvisionnement/PurchaseOrderDetail.jsx"));
const GoodsInReceiving = React.lazy(() => import("@/pages/fournisseur/approvisionnement/GoodsInReceiving.jsx"));
const LandedCostView = React.lazy(() => import("@/pages/fournisseur/approvisionnement/LandedCostView.jsx"));

// Module 02 — Catalogue & Pricing
const CatalogueList = React.lazy(() => import("@/pages/fournisseur/catalogue/CatalogueList.jsx"));
const ConnexionsList = React.lazy(() => import("@/pages/fournisseur/catalogue/ConnexionsList.jsx"));
const PpaReferenceManager = React.lazy(() => import("@/pages/fournisseur/catalogue/PpaReferenceManager.jsx"));

// Module 03 — Commercial (Pré-commande → Offre → Bon de Commande)
const PreCommandeInbox = React.lazy(() => import("@/pages/fournisseur/commercial/PreCommandeInbox.jsx"));
const PreCommandeDetail = React.lazy(() => import("@/pages/fournisseur/commercial/PreCommandeDetail.jsx"));
const OffreBuilder = React.lazy(() => import("@/pages/fournisseur/commercial/OffreBuilder.jsx"));
const BonCommandeList = React.lazy(() => import("@/pages/fournisseur/commercial/BonCommandeList.jsx"));
const BonCommandeDocument = React.lazy(() => import("@/pages/fournisseur/commercial/BonCommandeDocument.jsx"));
const PromotionManager = React.lazy(() => import("@/pages/fournisseur/commercial/PromotionManager.jsx"));
// Refonte WP-36 — « Réclamations reçues » (D-20, A-20; ux/03 §5.8): free core, read = SUP_ALL
// (fournisseur_commercial.read), the supplier's outcomes gated on fournisseur_commercial.manage inside
// the document (the server is authoritative).
const ReclamationsRecuesPage = React.lazy(() => import("@/pages/fournisseur/reclamations/ReclamationsRecuesPage.jsx"));
const ReclamationRecuePage = React.lazy(() => import("@/pages/fournisseur/reclamations/ReclamationRecuePage.jsx"));

// Module 04 — Warehouse & Lots (Entrepôts · Emplacements · Stock-lot · Mouvements · Transferts)
const EntrepotList = React.lazy(() => import("@/pages/fournisseur/entrepots/EntrepotList.jsx"));
const EmplacementTree = React.lazy(() => import("@/pages/fournisseur/entrepots/EmplacementTree.jsx"));
const StockParLot = React.lazy(() => import("@/pages/fournisseur/entrepots/StockParLot.jsx"));
const MouvementLedger = React.lazy(() => import("@/pages/fournisseur/entrepots/MouvementLedger.jsx"));
const TransfertWizard = React.lazy(() => import("@/pages/fournisseur/entrepots/TransfertWizard.jsx"));

// Module 05 — Logistics & Delivery (BL · Colis/QR · Tournée · Flotte · Chaîne du froid · Suivi · Rappels)
const BonLivraisonList = React.lazy(() => import("@/pages/fournisseur/logistique/BonLivraisonList.jsx"));
const BonLivraisonDetail = React.lazy(() => import("@/pages/fournisseur/logistique/BonLivraisonDetail.jsx"));
const ColisBoard = React.lazy(() => import("@/pages/fournisseur/logistique/ColisBoard.jsx"));
const ColisDetail = React.lazy(() => import("@/pages/fournisseur/logistique/ColisDetail.jsx"));
const TourneeWizard = React.lazy(() => import("@/pages/fournisseur/logistique/TourneeWizard.jsx"));
const FleetManager = React.lazy(() => import("@/pages/fournisseur/logistique/FleetManager.jsx"));
const ColdChainLog = React.lazy(() => import("@/pages/fournisseur/logistique/ColdChainLog.jsx"));
const SuiviLivraison = React.lazy(() => import("@/pages/fournisseur/logistique/SuiviLivraison.jsx"));
const RappelTrace = React.lazy(() => import("@/pages/fournisseur/logistique/RappelTrace.jsx"));
const ChauffeurApp = React.lazy(() => import("@/pages/fournisseur/logistique/chauffeur/ChauffeurApp.jsx"));

// Module 06 — Finance & Settlement (Receivables · Paiement/imputation ·
// Échéancier · Avoir · Trésorerie · Crédit · Comptabilité PCG)
const Receivables = React.lazy(() => import("@/pages/fournisseur/finance/Receivables.jsx"));
const PaiementsList = React.lazy(() => import("@/pages/fournisseur/finance/PaiementsList.jsx"));
const PaiementForm = React.lazy(() => import("@/pages/fournisseur/finance/PaiementForm.jsx"));
const Echeancier = React.lazy(() => import("@/pages/fournisseur/finance/Echeancier.jsx"));
const AvoirManager = React.lazy(() => import("@/pages/fournisseur/finance/AvoirManager.jsx"));
const Tresorerie = React.lazy(() => import("@/pages/fournisseur/finance/Tresorerie.jsx"));
const CreditControl = React.lazy(() => import("@/pages/fournisseur/finance/CreditControl.jsx"));
const Comptabilite = React.lazy(() => import("@/pages/fournisseur/finance/Comptabilite.jsx"));
// Module 06 EXT — Payables · Cheques+bordereaux · Bank-rec · Dunning ·
// Statements · Déclarations/G50
const Payables = React.lazy(() => import("@/pages/fournisseur/finance/Payables.jsx"));
const Cheques = React.lazy(() => import("@/pages/fournisseur/finance/Cheques.jsx"));
const BankRec = React.lazy(() => import("@/pages/fournisseur/finance/BankRec.jsx"));
const Dunning = React.lazy(() => import("@/pages/fournisseur/finance/Dunning.jsx"));
const Statements = React.lazy(() => import("@/pages/fournisseur/finance/Statements.jsx"));
const Declarations = React.lazy(() => import("@/pages/fournisseur/finance/Declarations.jsx"));

// Module 07 — Analytics & Rotation (Dashboard · Rotation · Forecasting ·
// Radar/E1 · KpiCockpit/E2 · ReportBuilder/E3 · BackTesting/E4 · WhatIf/E5 ·
// Profitability/E6 · Compliance). The advanced/forecast surfaces detect the 402
// { preview:true } inside the page and degrade to a real-but-limited PREVIEW
// (never a dead page), so the routes themselves stay un-gated.
const AnalyticsDashboard = React.lazy(() => import("@/pages/fournisseur/analytics/Dashboard.jsx"));
const AnalyticsRotation = React.lazy(() => import("@/pages/fournisseur/analytics/Rotation.jsx"));
const AnalyticsForecasting = React.lazy(() => import("@/pages/fournisseur/analytics/Forecasting.jsx"));
const AnalyticsRadar = React.lazy(() => import("@/pages/fournisseur/analytics/Radar.jsx"));
const AnalyticsKpiCockpit = React.lazy(() => import("@/pages/fournisseur/analytics/KpiCockpit.jsx"));
const AnalyticsReportBuilder = React.lazy(() => import("@/pages/fournisseur/analytics/ReportBuilder.jsx"));
const AnalyticsBackTesting = React.lazy(() => import("@/pages/fournisseur/analytics/BackTesting.jsx"));
const AnalyticsWhatIf = React.lazy(() => import("@/pages/fournisseur/analytics/WhatIf.jsx"));
const AnalyticsProfitability = React.lazy(() => import("@/pages/fournisseur/analytics/Profitability.jsx"));
const AnalyticsCompliance = React.lazy(() => import("@/pages/fournisseur/analytics/Compliance.jsx"));

// Module 08 — Subscription & Monetization (the supplier billing portal:
// MonAbonnement · Catalogue/plans · Historique/reçus · Parrainage). Read =
// fournisseur_subscription.read (all staff) so the routes stay un-gated; every
// manage action gates on fournisseur_subscription.manage inside the pages
// (server is authoritative). The Catalogue reads ?module= — the TC-SUB-07
// upgrade seam every 402-preview « Activer » CTA lands on.
const MonAbonnement = React.lazy(() => import("@/pages/fournisseur/abonnement/MonAbonnement.jsx"));
const AbonnementCatalogue = React.lazy(() => import("@/pages/fournisseur/abonnement/Catalogue.jsx"));
const AbonnementHistorique = React.lazy(() => import("@/pages/fournisseur/abonnement/Historique.jsx"));
const AbonnementParrainage = React.lazy(() => import("@/pages/fournisseur/abonnement/Parrainage.jsx"));

import LegacySection from "@/components/fournisseur/layout/LegacySection.jsx";

/**
 * Suspense boundary for the code-split portal pages. It sits on a pathless
 * route *inside* the layout route, so the fallback replaces only the <Outlet />
 * content — the shell (sidebar, header) stays mounted while a page chunk
 * downloads, instead of the whole portal blinking out.
 */
function PortalPageBoundary() {
  return (
    <React.Suspense
      fallback={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "40vh",
            opacity: 0.4,
            fontSize: 13,
          }}
        >
          Chargement…
        </div>
      }
    >
      <Outlet />
    </React.Suspense>
  );
}

export default function FournisseurRoutes() {
  return (
    <Routes>
      <Route
        element={
          <RequireSupplierRole>
            <FournisseurLayout />
          </RequireSupplierRole>
        }
      >
        <Route element={<PortalPageBoundary />}>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<FournisseurDashboard />} />

        {/* E-Factures — static routes before :id so /nouvelle resolves */}
        <Route path="efactures" element={<EFactureList />} />
        <Route path="efactures/nouvelle" element={<EFactureBuilder />} />
        <Route path="efactures/:id/modifier" element={<EFactureBuilder />} />
        <Route path="efactures/:id" element={<EFactureDetail />} />

        {/* ── Module 01 — Approvisionnement (Upstream Procurement) ──────────
            Whole subtree already behind <RequireSupplierRole> (read = SUP_ALL).
            Manage routes (manufacturers + PO authoring) gate on
            fournisseur_procurement.manage; receiving gates on
            fournisseur_procurement.receive. Static segments precede :id. */}
        <Route
          path="approvisionnement"
          element={<Navigate to="commandes" replace />}
        />
        <Route
          path="approvisionnement/fabricants"
          element={<ManufacturierList />}
        />
        <Route
          path="approvisionnement/commandes"
          element={<PurchaseOrderList />}
        />
        <Route
          path="approvisionnement/commandes/nouveau"
          element={
            <RequireSupplierRole capability="fournisseur_procurement.manage">
              <PurchaseOrderBuilder />
            </RequireSupplierRole>
          }
        />
        <Route
          path="approvisionnement/commandes/:id/modifier"
          element={
            <RequireSupplierRole capability="fournisseur_procurement.manage">
              <PurchaseOrderBuilder />
            </RequireSupplierRole>
          }
        />
        <Route
          path="approvisionnement/commandes/:id/reception"
          element={
            <RequireSupplierRole capability="fournisseur_procurement.receive">
              <GoodsInReceiving />
            </RequireSupplierRole>
          }
        />
        <Route
          path="approvisionnement/commandes/:id"
          element={<PurchaseOrderDetail />}
        />
        <Route path="approvisionnement/couts" element={<LandedCostView />} />

        {/* Organisation */}
        <Route
          path="equipe"
          element={
            <RequireSupplierRole capability="fournisseur_staff.manage">
              <StaffManagement />
            </RequireSupplierRole>
          }
        />
        <Route path="parametres/notifications" element={<NotificationPreferences />} />

        {/* Domains migrated in their own modules — rendered via legacy adapter,
            module-gated so a missing subscription shows a preview shell. */}
        <Route
          path="catalogue"
          element={
            <RequireModule code="CATALOGUE_FOURNISSEUR">
              <CatalogueList />
            </RequireModule>
          }
        />
        {/* M1 distributor PPA reference dataset — feeds the catalogue's PPA. */}
        <Route
          path="catalogue/ppa"
          element={
            <RequireModule code="CATALOGUE_FOURNISSEUR">
              <PpaReferenceManager />
            </RequireModule>
          }
        />
        {/* CAT-1 supplier↔pharmacy connections (open to all supplier roles). */}
        <Route path="connexions" element={<ConnexionsList />} />

        {/* ── Module 03 — Commercial (Pré-commande → Offre → Bon de Commande) ──
            Whole subtree already behind <RequireSupplierRole> (read = SUP_ALL).
            Offer building / negotiation / BC confirm gate on
            fournisseur_commercial.manage (server is authoritative; the pages
            also surface a preview when the role can't act). Static segments
            precede :id. The legacy `commandes`/`negociations` routes are
            replaced by these. */}
        <Route
          path="commercial"
          element={<Navigate to="pre-commandes" replace />}
        />
        <Route
          path="commercial/pre-commandes"
          element={
            <RequireModule code="PRECOMMANDES">
              <PreCommandeInbox />
            </RequireModule>
          }
        />
        <Route
          path="commercial/pre-commandes/:id/offre"
          element={
            <RequireSupplierRole capability="fournisseur_commercial.manage">
              <OffreBuilder />
            </RequireSupplierRole>
          }
        />
        <Route
          path="commercial/pre-commandes/:id"
          element={
            <RequireModule code="PRECOMMANDES">
              <PreCommandeDetail />
            </RequireModule>
          }
        />
        <Route
          path="commercial/bons-commande"
          element={<BonCommandeList />}
        />
        <Route
          path="commercial/bons-commande/:id"
          element={<BonCommandeDocument />}
        />
        {/* Refonte WP-36 — « Réclamations reçues » (ux/03 §5.8): the list and the document, declared
            before the catch-all (which redirected this path to the dashboard — AUD-RECL-01). */}
        <Route path="reclamations" element={<ReclamationsRecuesPage />} />
        <Route path="reclamations/:id" element={<ReclamationRecuePage />} />
        <Route
          path="commercial/promotions"
          element={
            <RequireModule code="TARIFICATION_AVANCEE">
              <PromotionManager />
            </RequireModule>
          }
        />

        {/* ── Module 04 — Entrepôts (Warehouse & Lots) ──────────────────────
            Whole subtree already behind <RequireSupplierRole> (read = SUP_ALL).
            Manage actions (entrepôt/emplacement CRUD, place/move/reserve,
            transfer) gate on fournisseur_warehouse.manage inside each page
            (server is authoritative). The 2nd+ entrepôt create 402-gates on
            MULTI_ENTREPOT_PRO → the list degrades to preview (free core
            otherwise — locations, lots, movements, transfers, FEFO, expiry).
            Static segments precede :id. */}
        <Route path="entrepots" element={<EntrepotList />} />
        <Route path="entrepots/stock" element={<StockParLot />} />
        <Route path="entrepots/mouvements" element={<MouvementLedger />} />
        <Route path="entrepots/transferts" element={<TransfertWizard />} />
        <Route path="entrepots/:id/emplacements" element={<EmplacementTree />} />

        {/* ── Module 05 — Logistique (Logistics & Delivery) ─────────────────
            Whole subtree already behind <RequireSupplierRole> (read = SUP_ALL).
            BL authoring + cold-chain + the recall trace are FREE CORE; the
            colis/tournée/tracking surfaces gate on LOGISTIQUE_PRO and the fleet
            on PARC_VEHICULES — each page detects the 402 { preview:true } and
            degrades to a real-but-limited PREVIEW (never a dead page), so the
            routes themselves stay un-gated. Manage actions gate on
            fournisseur_colis.manage / .scan inside each page (server is
            authoritative). Static segments precede :id. The driver app
            (/logistique/chauffeur) is the offline-capable E3 view. */}
        <Route path="logistique" element={<Navigate to="livraisons" replace />} />
        <Route path="logistique/livraisons" element={<BonLivraisonList />} />
        <Route path="logistique/livraisons/:id" element={<BonLivraisonDetail />} />
        <Route path="logistique/colis" element={<ColisBoard />} />
        <Route path="logistique/colis/:id" element={<ColisDetail />} />
        <Route path="logistique/tournees" element={<TourneeWizard />} />
        <Route path="logistique/flotte" element={<FleetManager />} />
        <Route path="logistique/chaine-froid" element={<ColdChainLog />} />
        <Route path="logistique/suivi" element={<SuiviLivraison />} />
        <Route path="logistique/rappels" element={<RappelTrace />} />
        <Route path="logistique/chauffeur" element={<ChauffeurApp />} />

        {/* ── Module 06 — Finance & Settlement ──────────────────────────────
            Whole subtree already behind <RequireSupplierRole> (read = SUP_ALL).
            Receivables / payments / imputation / échéancier / avoir / trésorerie
            / credit control are FREE CORE; manage actions gate on
            fournisseur_compta.manage / fournisseur_tresorerie.manage inside each
            page (server is authoritative). The Comptabilité workspace
            (journal / grand-livre / balance / period-close) detects the 402
            { preview:true } and degrades to a real-but-limited PREVIEW (never a
            dead page), so the route itself stays un-gated. Static segments
            precede :id. */}
        <Route path="finance" element={<Navigate to="creances" replace />} />
        <Route path="finance/creances" element={<Receivables />} />
        <Route path="finance/paiements" element={<PaiementsList />} />
        <Route path="finance/paiements/nouveau" element={<PaiementForm />} />
        <Route path="finance/echeancier" element={<Echeancier />} />
        <Route path="finance/avoirs" element={<AvoirManager />} />
        <Route path="finance/tresorerie" element={<Tresorerie />} />
        <Route path="finance/credit" element={<CreditControl />} />
        <Route path="finance/comptabilite" element={<Comptabilite />} />
        {/* EXT surfaces — all free core except Déclarations/G50 (preview-gates
            on COMPTABILITE_PRO inside the page). */}
        <Route path="finance/payables" element={<Payables />} />
        <Route path="finance/cheques" element={<Cheques />} />
        <Route path="finance/rapprochement" element={<BankRec />} />
        <Route path="finance/relances" element={<Dunning />} />
        <Route path="finance/releves" element={<Statements />} />
        <Route path="finance/declarations" element={<Declarations />} />

        {/* ── Module 07 — Analytics & Rotation ──────────────────────────────
            Whole subtree already behind <RequireSupplierRole> (read = SUP_ALL).
            The basic 30-day / top-10 Dashboard + the partial-free Rotation are
            FREE CORE; the advanced BI (Dashboard-advanced, Profitability/E6,
            KpiCockpit/E2, ReportBuilder/E3, Radar/E1, full Compliance) gate on
            ANALYTICS_AVANCE and the forecast-derived surfaces (Forecasting,
            BackTesting/E4, WhatIf/E5) gate on PREVISION_STOCK — each page detects
            the 402 { preview:true } and degrades to a real-but-limited PREVIEW
            (never a dead page), so the routes themselves stay un-gated. Static
            segments precede :id. */}
        <Route path="analytics" element={<Navigate to="tableau-de-bord" replace />} />
        <Route path="analytics/tableau-de-bord" element={<AnalyticsDashboard />} />
        <Route path="analytics/rotation" element={<AnalyticsRotation />} />
        <Route path="analytics/previsions" element={<AnalyticsForecasting />} />
        <Route path="analytics/previsions/backtest" element={<AnalyticsBackTesting />} />
        <Route path="analytics/previsions/simulation" element={<AnalyticsWhatIf />} />
        <Route path="analytics/radar" element={<AnalyticsRadar />} />
        <Route path="analytics/cockpit" element={<AnalyticsKpiCockpit />} />
        <Route path="analytics/rapports" element={<AnalyticsReportBuilder />} />
        <Route path="analytics/profitabilite" element={<AnalyticsProfitability />} />
        <Route path="analytics/conformite" element={<AnalyticsCompliance />} />

        {/* ── Module 08 — Abonnement (Subscription & Monetization) ──────────
            Whole subtree already behind <RequireSupplierRole> (read = SUP_ALL —
            any staff consults the org's subscription / catalogue / paiements /
            parrainage). Manage actions (essai / souscrire / addon / renouveler /
            changer-plan / annuler / réactiver / code parrainage) gate on
            fournisseur_subscription.manage inside each page (server is
            authoritative). Static segments only. */}
        <Route path="abonnement" element={<MonAbonnement />} />
        <Route path="abonnement/catalogue" element={<AbonnementCatalogue />} />
        <Route path="abonnement/historique" element={<AbonnementHistorique />} />
        <Route path="abonnement/parrainage" element={<AbonnementParrainage />} />

        {/* Legacy aliases — keep old deep-links working by redirecting into the
            new Module 03 Commercial workspace (pré-commandes inbox). */}
        <Route path="commandes" element={<Navigate to="commercial/pre-commandes" replace />} />
        <Route path="negociations" element={<Navigate to="commercial/pre-commandes" replace />} />

        <Route path="clients" element={<LegacySection name="clients" />} />

        <Route path="*" element={<Navigate to="dashboard" replace />} />
        </Route>
      </Route>
    </Routes>
  );
}
